import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getInventoryItems, getOrders } from '../../services/api';
import websocket from '../../services/websocket';
import { storage } from '../../utils/storage';
import './DockWidgets.css';

const AlertsWidget = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [realtimeAlerts, setRealtimeAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    loadAlerts();

    // Connect WebSocket and listen for waiter calls and bill requests
    const restaurantId = storage.get('selectedRestaurantId');

    const handleWaiterCall = (data) => {
      console.log('[AlertsWidget] Waiter call received:', data);
      const alert = {
        id: `waiter-${Date.now()}`,
        type: 'info',
        icon: 'room_service',
        messageKey: 'dock.waiterCall',
        messageDefault: 'Mesa {{table}} solicita asistencia',
        messageParams: { table: data.data.tableNumber },
        timestamp: Date.now(),
        action: () => navigate('/orders'),
      };

      setRealtimeAlerts((prev) => [alert, ...prev].slice(0, 3)); // Keep max 3 realtime alerts

      // Auto-remove after 30 seconds
      setTimeout(() => {
        setRealtimeAlerts((prev) => prev.filter((a) => a.id !== alert.id));
      }, 30000);
    };

    const handleBillRequest = (data) => {
      console.log('[AlertsWidget] Bill request received:', data);
      const alert = {
        id: `bill-${Date.now()}`,
        type: 'warning',
        icon: 'receipt',
        messageKey: 'dock.billRequest',
        messageDefault: 'Mesa {{table}} solicita la cuenta',
        messageParams: { table: data.data.tableNumber },
        timestamp: Date.now(),
        action: () => navigate('/orders'),
      };

      setRealtimeAlerts((prev) => [alert, ...prev].slice(0, 3)); // Keep max 3 realtime alerts

      // Auto-remove after 30 seconds
      setTimeout(() => {
        setRealtimeAlerts((prev) => prev.filter((a) => a.id !== alert.id));
      }, 30000);
    };

    if (restaurantId) {
      websocket.connect(restaurantId);
      websocket.on('WAITER_CALL', handleWaiterCall);
      websocket.on('BILL_REQUEST', handleBillRequest);
    }

    const interval = setInterval(loadAlerts, 120000); // Refresh inventory alerts every 2 minutes

    return () => {
      clearInterval(interval);
      websocket.off('WAITER_CALL', handleWaiterCall);
      websocket.off('BILL_REQUEST', handleBillRequest);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAlerts = async () => {
    try {
      setError(false);
      const alertsList = [];

      // Check inventory for low stock and expiring items
      const inventoryData = await getInventoryItems();
      const items = inventoryData.items || [];

      // Delivered but unpaid orders — revenue risk
      try {
        const ordersData = await getOrders();
        const ordersList = ordersData.orders || ordersData || [];
        ordersList.forEach((order) => {
          if (order.status === 'delivered' && order.paymentStatus !== 'paid') {
            alertsList.push({
              id: `unpaid-${order.orderId}`,
              type: 'danger',
              icon: 'warning',
              messageKey: 'orders.deliveredUnpaidAlert',
              messageDefault: 'Pedido {{id}} entregado sin cobrar',
              messageParams: { id: order.orderId?.slice(-6) },
              action: () => navigate('/orders'),
            });
          }
        });
      } catch (ordersErr) {
        console.error('Error loading orders for alerts:', ordersErr);
      }

      // Low stock alerts
      items.forEach((item) => {
        if (item.currentStock <= item.minStock) {
          alertsList.push({
            id: `stock-${item.itemId}`,
            type: 'warning',
            icon: 'inventory_2',
            messageKey: 'dock.lowStock',
            messageDefault: 'Stock bajo: {{name}}',
            messageParams: { name: item.name },
            action: () => navigate('/inventory'),
          });
        }
      });

      // Expiring soon alerts (within 3 days)
      const threeDaysFromNow = new Date();
      threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);

      items.forEach((item) => {
        if (item.expirationDate) {
          const parsed = new Date(item.expirationDate);
          const expDate = new Date(
            parsed.getUTCFullYear(),
            parsed.getUTCMonth(),
            parsed.getUTCDate()
          );
          if (expDate <= threeDaysFromNow && expDate >= new Date()) {
            alertsList.push({
              id: `exp-${item.itemId}`,
              type: 'danger',
              icon: 'event_busy',
              messageKey: 'dock.expiringSoon',
              messageDefault: 'Vence pronto: {{name}}',
              messageParams: { name: item.name },
              action: () => navigate('/inventory'),
            });
          }
        }
      });

      setAlerts(alertsList.slice(0, 5)); // Max 5 alerts
    } catch (err) {
      console.error('Error loading alerts:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="widget-loading">
        <span className="material-icons spinning">sync</span>
      </div>
    );
  }

  // Combine realtime alerts (high priority) with regular alerts
  const allAlerts = [...realtimeAlerts, ...alerts].slice(0, 5);

  // Show error only if no realtime alerts are available as fallback
  if (error && realtimeAlerts.length === 0) {
    return (
      <div className="widget-error">
        <span className="material-icons">cloud_off</span>
        <span>{t('dock.loadError', 'Error al cargar datos')}</span>
        <button type="button" className="widget-action-sm" onClick={loadAlerts}>
          {t('common.retry', 'Reintentar')}
        </button>
      </div>
    );
  }

  return (
    <div className="alerts-widget" aria-live="polite" aria-relevant="additions removals">
      {allAlerts.length === 0 ? (
        <div className="widget-empty success">
          <span className="material-icons">check_circle</span>
          <span>{t('dock.noAlerts', 'Sin alertas')}</span>
        </div>
      ) : (
        <div className="alerts-list" role="log">
          {allAlerts.map((alert) => (
            <div
              key={alert.id}
              className={`alert-item alert-${alert.type}`}
              role={alert.type === 'danger' ? 'alert' : undefined}
              onClick={alert.action}
            >
              <span className="material-icons alert-icon">{alert.icon}</span>
              <span className="alert-message">
                {t(alert.messageKey, alert.messageDefault, alert.messageParams)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AlertsWidget;
